import React, { useState, useEffect } from 'react';
import { Alert, Button, Col, ListGroup, Row, Spinner, StatusBadge } from '@/components/ui';
import { usersService } from '../services/usersService';
import ResponsiveModal from './ResponsiveModal';
import { formatDateTime } from '@/utils/displayDateTime';

interface UserHistory {
  id: number;
  userId: number;
  field?: string;
  oldValue?: string;
  newValue?: string;
  comments?: string;
  stage: string;
  external: boolean;
  changedBy?: number;
  createdAt: string;
  changedByUser?: {
    id: number;
    name: string;
    lastName: string;
    email: string;
  };
}

interface UserHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  userId: number;
  userName?: string;
}

const UserHistoryModal: React.FC<UserHistoryModalProps> = ({
  visible,
  onClose,
  userId,
  userName
}) => {
  const [history, setHistory] = useState<UserHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && userId) {
      fetchHistory();
    }
  }, [visible, userId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const historyData = await usersService.getUserHistory(userId);
      setHistory(historyData);
    } catch (err: any) {
      console.error('Error fetching user history:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'Failed to load user history';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatFieldName = (field: string | undefined, stage?: string): string => {
    if (!field && stage === 'CREATED') return 'Employee created';
    if (field === 'CREATED' || stage === 'CREATED') return 'Employee created';
    if (!field) return 'Change';
    const fieldMap: { [key: string]: string } = {
      name: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      employeeNumber: 'Employee Number',
      status: 'Status',
      roleId: 'Role',
      foremanAreas: 'Foreman Areas'
    };
    return fieldMap[field] || field;
  };

  const formatValue = (value: string | null | undefined): string => {
    if (value === null || value === undefined) return '(empty)';
    return value;
  };

  const sortedHistory = [...history].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={`User History${userName ? ` - ${userName}` : ''}`}
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Row>
        <Col xs={12}>
          {loading ? (
            <div className="tw-text-center tw-py-4">
              <Spinner animation="border">Loading...</Spinner>
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : sortedHistory.length === 0 ? (
            <Alert variant="info">No history records found for this user.</Alert>
          ) : (
            <div className="user-history" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <ListGroup>
                {sortedHistory.map((historyItem) => (
                  <ListGroup.Item key={historyItem.id}>
                    <div className="tw-flex tw-justify-between tw-items-start">
                      <div className="tw-grow">
                        <div className="tw-flex tw-items-center tw-mb-2">
                          <h6 className="tw-mb-0 tw-mr-2">
                            {formatFieldName(historyItem.field, historyItem.stage)}
                          </h6>
                          <StatusBadge status={historyItem.stage} label={historyItem.stage} />
                        </div>

                        {historyItem.field && historyItem.stage !== 'CREATED' && (
                          <div className="tw-mb-2">
                            <div className="tw-text-sm tw-text-content-muted">
                              <strong>From:</strong> {formatValue(historyItem.oldValue)}
                            </div>
                            <div className="tw-text-sm tw-text-content-muted">
                              <strong>To:</strong> {formatValue(historyItem.newValue)}
                            </div>
                          </div>
                        )}

                        {historyItem.comments && (
                          <p className="tw-mb-2 tw-text-content-muted tw-text-sm">{historyItem.comments}</p>
                        )}

                        <small className="tw-text-content-muted">
                          {historyItem.changedByUser ? (
                            `Changed by ${historyItem.changedByUser.name} ${historyItem.changedByUser.lastName} (${historyItem.changedByUser.email})`
                          ) : (
                            'Changed by System'
                          )}
                        </small>
                      </div>
                      <small className="tw-text-content-muted tw-ml-2 tw-whitespace-nowrap">
                        {formatDateTime(historyItem.createdAt)}
                      </small>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </Col>
      </Row>
    </ResponsiveModal>
  );
};

export default UserHistoryModal;

